import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { BsArrowLeft } from "react-icons/bs";
import { Skeleton } from "../../Components/Needed";
import ticketApi from "../../Helpers/ticketApi";
import { PRIORITY_LABEL } from "./shared";
import "../../Assets/css/tickets.css";

const isoDay = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const pctText = (value) => (value === null || value === undefined ? "-" : `${value}%`);

const Bars = ({ items, label = (i) => i.value, total }) => {
    const max = Math.max(1, ...items.map((i) => i.count));
    return (
        <ul className="tk-bars">
            {items.map((i) => (
                <li key={i.key || i.value}>
                    <span className="tk-bar-label">{label(i)}</span>
                    <span className="tk-bar-track" aria-hidden="true"><span className="tk-bar-fill" style={{ width: `${(i.count / max) * 100}%` }} /></span>
                    <span className="tk-bar-value">{i.count}{total ? <span className="text-muted"> ({Math.round((i.count / Math.max(total, 1)) * 100)}%)</span> : null}</span>
                </li>
            ))}
        </ul>
    );
};

// Ticket reports (Phase 7.8): tickets raised in the chosen days; open-by-age is as of now.
const TicketReports = () => {
    const navigate = useNavigate();
    const today = new Date();
    const [from, setFrom] = useState(isoDay(new Date(today.getTime() - 29 * 86400000)));
    const [to, setTo] = useState(isoDay(today));
    const [kind, setKind] = useState("customer");
    const [data, setData] = useState(null);

    const load = useCallback(async () => {
        setData(null);
        const r = await ticketApi.reports({ from: new Date(`${from}T00:00:00`).toISOString(), to: new Date(`${to}T23:59:59`).toISOString(), kind });
        if (!r.ok) { toast.error(r.message); setData({ error: true }); return; }
        setData(r.data);
    }, [from, to, kind]);
    useEffect(() => { load(); }, [load]);

    return (
        <div className="settings-page tk-page">
            <button type="button" className="btn btn-link px-0 mb-2" onClick={() => navigate("/tickets")}><BsArrowLeft aria-hidden="true" /> Tickets</button>
            <div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-3">
                <div>
                    <h4 className="fw-semibold mb-1">Ticket reports</h4>
                    <p className="text-muted mb-0">Tickets raised between the two dates.</p>
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-end">
                    <div>
                        <label className="form-label small mb-0" htmlFor="tk-from">From</label>
                        <input id="tk-from" type="date" className="form-control form-control-sm" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
                    </div>
                    <div>
                        <label className="form-label small mb-0" htmlFor="tk-to">To</label>
                        <input id="tk-to" type="date" className="form-control form-control-sm" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
                    </div>
                    <select className="form-select form-select-sm w-auto" aria-label="Ticket kind" value={kind} onChange={(e) => setKind(e.target.value)}>
                        <option value="customer">Customer tickets</option>
                        <option value="staff">Staff tickets</option>
                    </select>
                </div>
            </div>

            {!data ? <Skeleton height={420} /> : data.error ? null : (
                <>
                    <div className="tk-tiles">
                        {[
                            ["Raised", data.totals.created],
                            ["Open now", data.totals.open],
                            ["Resolved", data.totals.resolved],
                            ["Reopened", data.totals.reopened],
                            ["Appealed / escalated", data.totals.appealed],
                            ["Missed an SLA", data.totals.breached],
                        ].map(([label, value]) => <div key={label} className="tk-tile"><div className="tk-tile-value">{value}</div><div className="tk-tile-label">{label}</div></div>)}
                    </div>

                    <div className="tk-report-grid">
                        <section className="tk-card">
                            <h6>SLA</h6>
                            <dl className="tk-dl tk-dl-wide">
                                <dt>First reply on time</dt><dd>{pctText(data.sla.responseMetPercent)} <span className="small text-muted">of {data.sla.responseCounted}</span></dd>
                                <dt>Resolved on time</dt><dd>{pctText(data.sla.resolutionMetPercent)} <span className="small text-muted">of {data.sla.resolutionCounted}</span></dd>
                                <dt>Average first reply</dt><dd>{data.sla.avgFirstResponseMinutes ?? "-"} min</dd>
                                <dt>Average resolution</dt><dd>{data.sla.avgResolutionHours ?? "-"} h</dd>
                            </dl>
                        </section>
                        <section className="tk-card">
                            <h6>Open tickets by age (now)</h6>
                            <Bars items={data.openByAge} label={(i) => i.label} />
                        </section>
                        <section className="tk-card">
                            <h6>Rating</h6>
                            <p className="mb-2"><span className="fs-4 fw-semibold">{data.rating.average ?? "-"}</span> <span className="text-muted small">average of {data.rating.count}</span></p>
                            <Bars items={[...data.rating.stars].reverse().map((s) => ({ key: s.stars, value: `${s.stars} star${s.stars === 1 ? "" : "s"}`, count: s.count }))} />
                        </section>
                        <section className="tk-card">
                            <h6>By category</h6>
                            <Bars items={data.categories} total={data.totals.created} />
                        </section>
                        {kind === "customer" ? (
                            <section className="tk-card">
                                <h6>By hub</h6>
                                <Bars items={data.hubs} label={(i) => i.name || i.value} total={data.totals.created} />
                            </section>
                        ) : null}
                        <section className="tk-card">
                            <h6>By priority</h6>
                            <Bars items={data.priorities} label={(i) => PRIORITY_LABEL[i.value] || i.value} total={data.totals.created} />
                        </section>
                    </div>

                    <section className="tk-card mt-3">
                        <h6>Handled per person</h6>
                        {data.handlers.length ? (
                            <div className="table-responsive">
                                <table className="table table-sm align-middle mb-0">
                                    <thead><tr><th scope="col">Name</th><th scope="col">Handled</th><th scope="col">Resolved</th><th scope="col">Still open</th><th scope="col">Missed SLA</th><th scope="col">Rating</th></tr></thead>
                                    <tbody>
                                        {data.handlers.map((p) => (
                                            <tr key={`${p.type}${p.id}`}>
                                                <td>{p.name}{p.type === "admin" ? <span className="text-muted small"> (admin)</span> : null}</td>
                                                <td>{p.handled}</td><td>{p.resolved}</td><td>{p.open}</td><td>{p.breached}</td>
                                                <td>{p.rating ?? "-"}{p.ratings ? <span className="text-muted small"> ({p.ratings})</span> : null}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : <p className="small text-muted mb-0">No tickets were taken in these days.</p>}
                    </section>

                    {kind === "customer" && data.bookings.length ? (
                        <section className="tk-card mt-3">
                            <h6>Bookings with more than one ticket</h6>
                            <ul className="mb-0">
                                {data.bookings.map((b) => <li key={b.value}><Link to={`/booking/details/${b.value}`}>{b.value}</Link>: {b.count} tickets</li>)}
                            </ul>
                        </section>
                    ) : null}
                </>
            )}
        </div>
    );
};

export default TicketReports;
